import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import IconButton from "@mui/material/IconButton";
import SendIcon from "@mui/icons-material/Send";
import Button from "@mui/material/Button";
import { ToastContainer, toast } from "react-toastify";
import Tooltip from "@mui/material/Tooltip";
import { Stack } from "@mui/material";
import MDTypography from "components/MDTypography";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

const SendRequestToHMS = () => {
  const [formNo, setFormNo] = useState("");
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchStudents = async () => {
    if (!formNo) {
      toast.error("Please enter a form number.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        form_id: parseInt(formNo),
      };

      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      };

      const response = await fetch(
        BASE_URL + `/submit_application/get_students_by_form_id`,
        requestOptions
      );

      const data = await response.json();

      if (data.students && data.students.length > 0) {
        setStudents(data.students);
        toast.success(`Found ${data.students.length} student(s).`);
      } else {
        setStudents([]);
        toast.warning("No students found for this form number.");
      }
    } catch (error) {
      console.log("Error fetching students:", error);
      toast.error("Error fetching student data.");
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendRequestToHMS = async (student) => {
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await fetch(
        BASE_URL + `/submit_application/send_request_to_hms/${student.id}`,
        requestOptions
      );

      const data = await response.json();

      if (response.ok) {
        if (data.success) {
          toast.success(`Request sent to HMS successfully for ${student.student_name}`);
        } else {
          toast.error(data.error || "Failed to send request to HMS");
        }
      } else {
        toast.error(data.error || "Failed to send request to HMS");
      }
    } catch (error) {
      console.log("Error sending request to HMS:", error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        toast.error("Network error. Please check your connection.");
      } else {
        toast.error("Error sending request to HMS. Please try again.");
      }
    }
  };

  // Prepare data for DataTable
  const prepareTableData = () => {
    const columns = [
      { Header: "Roll No", accessor: "roll_no", width: "20%" },
      { Header: "Student Name", accessor: "student_name", width: "25%" },
      { Header: "Department", accessor: "department_name", width: "30%" },
      { Header: "Program", accessor: "program_name", width: "35%" },
      { Header: "Actions", accessor: "actions", width: "15%" },
    ];

    const rows = students.map((student) => ({
      roll_no: (
        <MDTypography variant="caption" fontWeight="medium">
          {student.roll_no}
        </MDTypography>
      ),
      student_name: (
        <MDBox display="flex" flexDirection="column">
          <MDTypography variant="caption" fontWeight="medium">
            {student.student_name}
          </MDTypography>
          <MDTypography variant="caption" color="text" fontWeight="regular">
            Form ID: {student.application_form_id}
          </MDTypography>
        </MDBox>
      ),
      department_name: (
        <MDTypography variant="caption" fontWeight="medium">
          {student.department_name}
        </MDTypography>
      ),
      program_name: (
        <MDTypography variant="caption" fontWeight="medium">
          {student.program_name}
        </MDTypography>
      ),
      actions: (
        <MDBox display="flex" alignItems="center">
          <Tooltip title="Send Request to HMS">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleSendRequestToHMS(student)}
            >
              <SendIcon />
            </IconButton>
          </Tooltip>
        </MDBox>
      ),
    }));

    return { columns, rows };
  };

  const { columns, rows } = prepareTableData();

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={2}
            >
              <div className="col-md-4">
                <MDTypography variant="subtitle2" fontWeight="light" mb={1}>
                  Form Number
                </MDTypography>
                <MDInput
                  type="number"
                  label="Enter Form Number"
                  fullWidth
                  value={formNo}
                  onChange={(e) => setFormNo(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <MDBox mt={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    style={{ width: "100%", color: "white" }}
                    onClick={fetchStudents}
                    disabled={isLoading}
                  >
                    {isLoading ? "Fetching..." : "Fetch Students"}
                  </Button>
                </MDBox>
              </div>
            </Stack>
          </Grid>

          {/* Display Students Table */}
          {students.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <MDBox pt={3} px={3}>
                  <MDTypography variant="h6" fontWeight="medium">
                    Students Found ({students.length})
                  </MDTypography>
                </MDBox>
                <MDBox pt={1} pb={2} px={3}>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* No Data Message */}
          {students.length === 0 && !isLoading && (
            <Grid item xs={12}>
              <Card>
                <MDBox pt={3} px={3} pb={3} textAlign="center">
                  <MDTypography variant="h6" fontWeight="light" color="text">
                    No students found. Please enter a form number and click Fetch Students.
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default SendRequestToHMS;